import axios, { AxiosResponse, Method } from 'axios';

export type AdminApiTarget = 'hub' | 'catalog' | 'absolute';

export type AdminFieldKey =
  | 'segment'
  | 'type'
  | 'language'
  | 'codes'
  | 'code'
  | 'CRVcode'
  | 'breedName'
  | 'name'
  | 'scope'
  | 'archiveOldCatalog';

export type AdminFieldType = 'text' | 'select' | 'boolean';

export type AdminFieldDefinition = {
  key: AdminFieldKey;
  label: string;
  type: AdminFieldType;
  required?: boolean;
  helperText?: string;
  defaultValue?: string | boolean;
  options?: {
    label: string;
    value: string;
  }[];
};

export type AdminActionCategory =
  | 'bull'
  | 'catalog'
  | 'user'
  | 'administration';

export type AdminAction = {
  id: string;
  category: AdminActionCategory;
  label: string;
  description: string;
  api: AdminApiTarget;
  method: Method;
  path: string;
  body: 'formData' | 'none' | 'json';
  requiresFile?: boolean;
  multipleFiles?: boolean;
  fileAccept?: string;
  fields?: AdminFieldDefinition[];
  dangerous?: boolean;
};

export type RunAdminActionInput = {
  action: AdminAction;
  values: Partial<Record<AdminFieldKey, string | boolean>>;
  files: File[];
};

export type AdminAsyncJobCreationResponse = {
  ok: boolean;
  jobId: string;
  status: 'queued';
  statusUrl: string;
  runner:
    | { started: true; operationName?: string }
    | { started: false; reason: string };
};

export type AdminJobStatus = 'queued' | 'running' | 'success' | 'error';

export type AdminJobResponse = {
  ok?: boolean;
  jobId?: string;
  id?: string;
  status: AdminJobStatus;
  result?: unknown;
  error?: unknown;
};

const isDevelopment = process.env.NODE_ENV === 'development';

const hubBaseUrl = isDevelopment
  ? '/admin-api-hub'
  : process.env.REACT_APP_API_HUB ||
    process.env.REACT_APP_API ||
    'http://localhost:8080';

const catalogBaseUrl = isDevelopment
  ? '/admin-api-catalog'
  : process.env.REACT_APP_API_CATALOG ||
    'https://api-2-prod-e6pbdwdwoa-rj.a.run.app';

const ADMIN_REQUEST_TIMEOUT_MS = 60 * 60 * 1000;
const ADMIN_JOB_STATUS_TIMEOUT_MS = 30 * 1000;

const hubClient = axios.create({
  baseURL: hubBaseUrl,
  timeout: ADMIN_REQUEST_TIMEOUT_MS,
});
const catalogClient = axios.create({
  baseURL: catalogBaseUrl,
  timeout: ADMIN_REQUEST_TIMEOUT_MS,
});

const segmentField: AdminFieldDefinition = {
  key: 'segment',
  label: 'Segmento',
  type: 'select',
  required: true,
  defaultValue: '1',
  options: [
    { label: 'Corte', value: '1' },
    { label: 'Leite', value: '2' },
  ],
};

const typeField: AdminFieldDefinition = {
  key: 'type',
  label: 'Tipo',
  type: 'select',
  required: true,
  defaultValue: '1',
  options: [
    { label: 'Europeu', value: '1' },
    { label: 'Zebu', value: '2' },
  ],
};

const languageField: AdminFieldDefinition = {
  key: 'language',
  label: 'Idioma',
  type: 'select',
  required: true,
  defaultValue: 'pt',
  options: [
    { label: 'Português', value: 'pt' },
    { label: 'Inglês', value: 'en' },
    { label: 'Espanhol', value: 'es' },
  ],
};

const nameField: AdminFieldDefinition = {
  key: 'name',
  label: 'Nome do catálogo',
  type: 'text',
  required: true,
  helperText: 'Ex.: Corte Zebu 2026',
};

const archiveOldCatalogField: AdminFieldDefinition = {
  key: 'archiveOldCatalog',
  label: 'Arquivar catálogo antigo',
  type: 'boolean',
  defaultValue: false,
};

const baseSegmentTypeFields = [ segmentField, typeField ];
const baseSegmentTypeLanguageFields = [ segmentField, typeField, languageField ];

export const adminActionCategories: {
  id: AdminActionCategory;
  label: string;
}[] = [
  { id: 'bull', label: 'Touro' },
  { id: 'catalog', label: 'Catálogo' },
  { id: 'user', label: 'Usuário' },
  { id: 'administration', label: 'Administração' },
];

export const adminActions: AdminAction[] = [
  {
    id: 'bull-base-import',
    category: 'bull',
    label: 'Importar base de touros',
    description: 'Envia o CSV principal da base de touros.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,text/csv',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-proof-ranges-import',
    category: 'bull',
    label: 'Importar intervalos de provas',
    description: 'Envia o CSV de intervalos das provas de leite europeu.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/european/milk/proof/ranges/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,text/csv',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-proof-import',
    category: 'bull',
    label: 'Importar provas',
    description: 'Envia o CSV de provas dos touros.',
    api: 'hub',
    method: 'POST',
    path: '/bull/proof/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,text/csv',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-image-import',
    category: 'bull',
    label: 'Importar imagens dos touros',
    description: 'Envia uma ou mais imagens de touros.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/image/import',
    body: 'formData',
    requiresFile: true,
    multipleFiles: true,
    fileAccept: 'image/*',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-stamp-import',
    category: 'bull',
    label: 'Importar selos',
    description: 'Envia imagens de selos.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/stamp/import',
    body: 'formData',
    requiresFile: true,
    multipleFiles: true,
    fileAccept: 'image/*',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-cover-image-import',
    category: 'catalog',
    label: 'Importar capa',
    description: 'Envia a imagem ou PDF de capa do segmento/tipo.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/cover/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: 'image/*,.pdf,application/pdf',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-complementary-image-import',
    category: 'bull',
    label: 'Importar imagem complementar',
    description: 'Envia imagem complementar, como a imagem Jequiti.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/complementary/image/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: 'image/*',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-instructions-import',
    category: 'catalog',
    label: 'Importar instruções PDF',
    description: 'Envia o PDF de instruções do catálogo.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/instructions/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.pdf,application/pdf',
    fields: baseSegmentTypeLanguageFields,
  },
  {
    id: 'bull-end-page-import',
    category: 'catalog',
    label: 'Importar página final PDF',
    description: 'Envia o PDF da página final do catálogo.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/end/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.pdf,application/pdf',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-middle-page-import',
    category: 'catalog',
    label: 'Importar página intermediária PDF',
    description: 'Envia PDF intermediário associado a um touro ou posição.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/middle/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.pdf,application/pdf',
    fields: [
      ...baseSegmentTypeLanguageFields,
      {
        key: 'CRVcode',
        label: 'Código CRV',
        type: 'text',
        helperText: 'Obrigatório quando a página for de um touro específico.',
      },
    ],
  },
  {
    id: 'bull-breed-cover-import',
    category: 'catalog',
    label: 'Importar capa de raça PDF',
    description: 'Envia PDF de capa para uma raça específica.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/breed/cover/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.pdf,application/pdf',
    fields: [
      ...baseSegmentTypeFields,
      {
        key: 'breedName',
        label: 'Raça',
        type: 'text',
        required: true,
      },
    ],
  },
  {
    id: 'bull-individual-pdf-import',
    category: 'catalog',
    label: 'Importar PDF individual digital',
    description: 'Envia PDF individual já gerado.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/pdf/import',
    body: 'formData',
    requiresFile: true,
    multipleFiles: true,
    fileAccept: '.pdf,application/pdf',
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-clean-imported-pages',
    category: 'catalog',
    label: 'Limpar páginas importadas',
    description: 'Remove páginas importadas para o catálogo.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/imports/clean',
    body: 'formData',
    dangerous: true,
    fields: [
      ...baseSegmentTypeLanguageFields,
      {
        key: 'scope',
        label: 'Escopo',
        type: 'select',
        required: true,
        defaultValue: 'middle',
        options: [
          { label: 'Páginas intermediárias', value: 'middle' },
          { label: 'Instruções', value: 'instructions' },
          { label: 'Página final', value: 'end' },
          { label: 'Todos', value: 'all' },
        ],
      },
    ],
  },
  {
    id: 'bull-proof-delete',
    category: 'bull',
    label: 'Deletar provas',
    description: 'Remove provas do segmento/tipo selecionado.',
    api: 'hub',
    method: 'DELETE',
    path: '/bull/proof/delete/:segment/:type',
    body: 'none',
    dangerous: true,
    fields: baseSegmentTypeFields,
  },
  {
    id: 'bull-individual-plate',
    category: 'bull',
    label: 'Gerar placas dos touros',
    description: 'Gera PDF de placas para os códigos informados.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/individual-plate/pdf/:language/:segment/:type/:codes',
    body: 'none',
    fields: [
      ...baseSegmentTypeLanguageFields,
      {
        key: 'codes',
        label: 'Códigos CRV',
        type: 'text',
        required: true,
        helperText: 'Separe múltiplos códigos por vírgula.',
      },
    ],
  },
  {
    id: 'bull-ministry-stamp-import',
    category: 'bull',
    label: 'Importar selos Ministério',
    description: 'Envia CSV dos selos do Ministério.',
    api: 'hub',
    method: 'POST',
    path: '/bull/v2/ministry/stamp',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,text/csv',
  },
  {
    id: 'catalog-all-digital',
    category: 'catalog',
    label: 'Gerar PDF digital de todos os touros',
    description: 'Gera o PDF digital com todos os touros do segmento/tipo.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/all/pdf/digital/:language/:segment/:type',
    body: 'none',
    fields: baseSegmentTypeLanguageFields,
  },
  {
    id: 'catalog-all-print',
    category: 'catalog',
    label: 'Gerar PDF impresso de todos os touros',
    description: 'Gera o PDF impresso com todos os touros do segmento/tipo.',
    api: 'catalog',
    method: 'GET',
    path: '/bull/all/:language/:segment/:type',
    body: 'none',
    fields: baseSegmentTypeLanguageFields,
  },
  {
    id: 'catalog-individual-pdf',
    category: 'catalog',
    label: 'Gerar página individual do touro',
    description: 'Gera o PDF individual para os códigos informados.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/individual/pdf/:language/:segment/:type/:codes',
    body: 'none',
    fields: [
      ...baseSegmentTypeLanguageFields,
      {
        key: 'codes',
        label: 'Códigos CRV',
        type: 'text',
        required: true,
        helperText: 'Separe múltiplos códigos por vírgula.',
      },
    ],
  },
  {
    id: 'catalog-complete-digital',
    category: 'catalog',
    label: 'Gerar catálogo completo digital',
    description: 'Gera o catálogo completo digital.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/catalog',
    body: 'formData',
    fields: [
      ...baseSegmentTypeLanguageFields,
      nameField,
      archiveOldCatalogField,
    ],
  },
  {
    id: 'catalog-all-and-catalog',
    category: 'catalog',
    label: 'Gerar PDF único completo',
    description: 'Gera touros e catálogo em um único fluxo.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/all-and-catalog/:language/:segment/:type',
    body: 'formData',
    fields: [ ...baseSegmentTypeLanguageFields, nameField, archiveOldCatalogField ],
  },
  {
    id: 'catalog-complete-print',
    category: 'catalog',
    label: 'Gerar catálogo completo para impressão',
    description: 'Gera o catálogo completo para impressão.',
    api: 'catalog',
    method: 'POST',
    path: '/bull/catalog',
    body: 'formData',
    fields: baseSegmentTypeLanguageFields,
  },
  {
    id: 'user-manager-import',
    category: 'user',
    label: 'Importar gerentes',
    description: 'Envia planilha de gerentes.',
    api: 'hub',
    method: 'POST',
    path: '/user/manager/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    id: 'user-consultant-import',
    category: 'user',
    label: 'Importar consultores',
    description: 'Envia planilha de consultores.',
    api: 'hub',
    method: 'POST',
    path: '/user/consultant/import',
    body: 'formData',
    requiresFile: true,
    fileAccept: '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  {
    id: 'user-consultant-image-import',
    category: 'user',
    label: 'Importar imagens dos consultores',
    description: 'Envia imagens dos consultores.',
    api: 'hub',
    method: 'POST',
    path: '/user/v2/consultant/image/import',
    body: 'formData',
    requiresFile: true,
    multipleFiles: true,
    fileAccept: 'image/*',
  },
  {
    id: 'admin-sync-database',
    category: 'administration',
    label: 'Sincronizar produção com testes',
    description: 'Executa sincronização de banco conforme API administrativa.',
    api: 'hub',
    method: 'POST',
    path: '/admin/sync/database',
    body: 'json',
    dangerous: true,
  },
];

const formatCodes = (value: string | boolean | undefined) => {
  if (typeof value !== 'string') return '';
  return value
    .split(',')
    .map(code => code.trim())
    .filter(Boolean)
    .join(',');
};

const buildPath = (
  path: string,
  values: Partial<Record<AdminFieldKey, string | boolean>>
) => path.replace(/:([A-Za-z]+)/g, (_, key: AdminFieldKey) => {
  const value = key === 'codes' || key === 'code'
    ? formatCodes(values[key])
    : values[key];

  return encodeURIComponent(String(value ?? ''));
});

const getClient = (api: AdminApiTarget) => {
  if (api === 'catalog') return catalogClient;
  if (api === 'hub') return hubClient;
  return axios;
};

const appendFormFields = (
  formData: FormData,
  action: AdminAction,
  values: Partial<Record<AdminFieldKey, string | boolean>>,
  files: File[]
) => {
  files.forEach(file => formData.append('file', file));

  action.fields?.forEach(field => {
    const value = values[field.key];
    if (value === undefined || value === '') return;
    formData.append(field.key, String(value));
  });
};

export const getDefaultAdminActionValues = (action: AdminAction) => {
  const defaults: Partial<Record<AdminFieldKey, string | boolean>> = {};
  action.fields?.forEach(field => {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    }
  });
  return defaults;
};

export const runAdminAction = async ({
  action,
  values,
  files,
}: RunAdminActionInput): Promise<AxiosResponse> => {
  const client = getClient(action.api);
  const url = buildPath(action.path, values);

  if (action.body === 'formData') {
    const formData = new FormData();
    appendFormFields(formData, action, values, files);

    return client.request({
      method: action.method,
      url,
      data: formData,
      timeout: ADMIN_REQUEST_TIMEOUT_MS,
    });
  }

  return client.request({
    method: action.method,
    url,
    data: action.body === 'json' ? {} : undefined,
    timeout: ADMIN_REQUEST_TIMEOUT_MS,
  });
};

export const getAdminJob = async (
  action: AdminAction,
  jobId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<AdminJobResponse>> => {
  const client = getClient(action.api);

  return client.request<AdminJobResponse>({
    method: 'GET',
    url: `/bull/jobs/${encodeURIComponent(jobId)}`,
    timeout: ADMIN_JOB_STATUS_TIMEOUT_MS,
    signal,
  });
};

const admin = {
  actions: adminActions,
  categories: adminActionCategories,
  getDefaultActionValues: getDefaultAdminActionValues,
  getJob: getAdminJob,
  runAction: runAdminAction,
};

export default admin;
